/**
 * /admin/login — admin åpnes med kontoen (samlet innlogging). Direkte innlogging med
 * brukernavn er stengt; en konto med superadmin eller klubbadmin får en admin-økt via
 * broen (POST /api/konto/admin-bro), som Admin-knappen øverst til høyre.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.ts';
import { aapneAdmin, kanAapneAdmin } from '../../lib/aapneAdmin.ts';
import { SiteLayout } from '../../components/SiteLayout.tsx';

export function AdminLogin() {
  const me = useQuery({ queryKey: ['konto-me'], queryFn: () => api.kontoMe(), retry: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function aapne() {
    setBusy(true);
    setErr(null);
    try {
      await aapneAdmin();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Kunne ikke åpne admin');
      setBusy(false);
    }
  }

  const u = me.data;
  const kanAdmin = kanAapneAdmin(u);

  return (
    <SiteLayout>
      <main className="flex justify-center px-4 py-12 sm:py-16">
        <div className="w-full max-w-sm bg-slate-900/60 border border-slate-800 rounded-2xl p-6 text-center">
          <h1 className="text-xl font-bold mb-5">Admin</h1>
          {me.isLoading ? (
            <p className="text-sm text-slate-400">Laster…</p>
          ) : !u ? (
            <>
              <p className="text-sm text-slate-300">
                Admin åpnes med kontoen din — samme innlogging som på racecloud.no og i appen.
              </p>
              <Link
                to="/logg-inn?til=/admin/login"
                className="mt-5 block w-full bg-sky-600 hover:bg-sky-500 py-3 rounded-lg font-semibold"
              >
                Logg inn
              </Link>
            </>
          ) : kanAdmin ? (
            <>
              <p className="text-sm text-slate-300">
                Innlogget som <b>{u.fullName}</b> ({u.isSuperadmin ? 'superadmin' : 'klubbadmin'}).
              </p>
              {err && (
                <div className="mt-3 text-red-300 bg-red-900/30 border border-red-800 rounded-lg p-3 text-sm">
                  {err}
                </div>
              )}
              <button
                onClick={aapne}
                disabled={busy}
                className="mt-5 w-full bg-sky-600 hover:bg-sky-500 py-3 rounded-lg font-semibold disabled:opacity-60"
              >
                {busy ? 'Åpner…' : 'Åpne admin'}
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-300">
                Kontoen din (<b>{u.fullName}</b>) har ikke admin-tilgang.
              </p>
              <Link
                to="/min"
                className="mt-5 block w-full bg-slate-800 hover:bg-slate-700 py-3 rounded-lg font-semibold"
              >
                Til Min side
              </Link>
            </>
          )}
        </div>
      </main>
    </SiteLayout>
  );
}
